import { apiService } from "core/services/apiService";
import { paginationService } from "core/services/paginationService";
import Cookies from "js-cookie";
import { useMutation } from "react-query";
import { CreateWorkplaceRequest, GracenoteBulkSportImportPayload, GracenoteBulkTeamImportPayload, GracenoteSportPreference, GracenoteTeam, PaginatedResponse, Sport, TeamPreference } from "types";
import { safeSetItem, clearOldCacheEntriesByCount } from "core/utils/localStorageUtils";
import additionalGracenoteChannels from "core/data/additionalGracenoteChannels.json";

export const useGracenoteBulkSportsImportMutation = () => {
    return useMutation({
        mutationFn: (payload: any) => {
            // New endpoint expects: { workplace: { id }, sports: [{ sport: { id }, maxNumberOfGames, listOrder }] }
            return apiService.post(`/game_master_preference_sports`, payload)
        }
    });
};

export const useGracenoteBulkSportsDeltaMutation = () => {
    return useMutation({
        mutationFn: (payload: any) => {
            // Delta endpoint for sports selection save
            return apiService.post(`/game_master_preference_sports/delta`, payload);
        }
    });
};


//Save Teams
export const useGracenoteBulkTeamsImportMutation = () => {
    return useMutation({
        mutationFn: (payload: any[]) => {
            return apiService.post(`/game_master_preference_teams`, payload)
        }
    });
};


// Delete team preference
export const deleteTeamPreference = async (
    teamId: number | string
): Promise<void> => {
    await apiService.delete(`/workplaces/gracenote/teams/preferences/${teamId}`);
};

// Delete preference channels by lineup ID
export const deletePreferenceChannelsByLineup = async (
    lineupId: string,
    workplaceId: number
): Promise<void> => {
    await apiService.delete(`/game_master_preference_channels/lineup/${lineupId}/workplace/${workplaceId}`);
};


export const fetchGracenoteSports = async (): Promise<Sport[]> => {
    const response = await apiService.get<any>(
        "/workplaces/gracenote/sports",
        { params: { page: 1, itemsPerPage: 500 } }
    );
    // Map API response to Sport type, preserving gracenote ID if available
    const sports = (Array.isArray(response.data) ? response.data : []).map((s: any) => ({
        sportsId: s.sportsId || s.id?.toString() || s.gracenote?.toString() || '',
        sportsName: s.sportsName || s.label || '',
        gracenote: s.gracenote || s.sportsId || Number(s.id)
    }));
    return sports.sort((a, b) => a.sportsName.localeCompare(b.sportsName));
};


export const fetchGracenoteSportsPreferences = async (workplaceId?: number): Promise<
    any[]
> => {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));

    try {
        const response = await apiService.get<any>(
            `/game_master_preference_sports`,
            { params: { workplace: id } }
        );

        // Handle both response formats: { member: [...] } or direct array
        let member: any[] = [];
        if (Array.isArray(response.data)) {
            member = response.data;
        } else if (Array.isArray(response.data?.member)) {
            member = response.data.member;
        }

        // Normalize to match GracenoteSportPreference interface expected by UI
        const normalized = member.map((m: any) => ({
            id: m.id,
            sport: m.sport ? {
                id: m.sport.id,
                label: m.sport.label,
                gracenote: m.sport.gracenote,
            } : undefined,
            max_number_of_games: m.maxNumberOfGames ?? m.max_number_of_games,
            list_order: m.listOrder ?? m.list_order,
        }));

        // Sort by list_order to match previous API behavior
        return normalized.sort((a, b) => (a.list_order || 0) - (b.list_order || 0));
    } catch (error) {
        console.warn("Sports preferences API not available, returning empty array:", error);
        return [];
    }
};




// Fetch team preferences
export const fetchTeamPreferences = async (): Promise<TeamPreference[]> => {
    const workplaceId = Number(localStorage.getItem("workplace-id"));

    try {
        // Fetch sports preferences to get sport labels (sport ID to label mapping)
        const sportsMap: Record<number, string> = {};
        try {
            const sportsPreferences = await fetchGracenoteSportsPreferences();
            sportsPreferences.forEach(sportPref => {
                if (sportPref.sport?.id) {
                    sportsMap[sportPref.sport.id] = sportPref.sport.label || '';
                }
            });
        } catch (sportsError) {
            console.warn("Could not fetch sports preferences for team preferences mapping:", sportsError);
        }

        const response = await apiService.get<any>(
            `/game_master_preference_teams?pagination=false&workplace=${workplaceId}`
        );

        // Handle response with member array
        const member = Array.isArray(response.data?.member) ? response.data.member : [];

        // Map API response to TeamPreference format
        return member.map((item: any) => {
            // Extract sport ID from IRI string (e.g., "/api/sports/63" -> 63)
            let sportId: number | undefined;
            if (item.team?.sport) {
                if (typeof item.team.sport === 'string') {
                    sportId = Number(item.team.sport.split('/').pop());
                } else if (item.team.sport.id) {
                    sportId = Number(item.team.sport.id);
                }
            }

            // Extract team organization ID from IRI string
            let teamOrgId: number | undefined;
            if (item.team?.teamOrganization) {
                if (typeof item.team.teamOrganization === 'string') {
                    teamOrgId = Number(item.team.teamOrganization.split('/').pop());
                } else if (item.team.teamOrganization.id) {
                    teamOrgId = Number(item.team.teamOrganization.id);
                }
            }

            return {
                id: item.id,
                team: {
                    id: item.team?.id,
                    team_brand_name: item.team?.teamBrandName || '',
                    nick_name: item.team?.nickName || '',
                    proper_name: item.team?.properName || '',
                    abbreviation: item.team?.abbreviation || '',
                    team_brand_id: item.team?.teamId || '',
                    team_id: item.team?.teamId || '',
                    sport: sportId ? {
                        id: sportId,
                        label: sportsMap[sportId] || '',
                    } : undefined,
                    team_organization: teamOrgId ? {
                        id: teamOrgId,
                        organization_name: '',
                        organization_id: '',
                    } : undefined,
                } as any,
                list_order: item.listOrder ?? 0,
            };
        });
    } catch (error) {
        console.warn("Team preferences API not available, returning empty array:", error);
        return [];
    }
};



// Fetch Gracenote teams
export const fetchGracenoteTeams = async (): Promise<GracenoteTeam[]> => {
    try {
        const data = await paginationService.getWithHandler('/game_master_teams', undefined, 30);
        const member = Array.isArray(data?.member)
            ? data.member
            : Array.isArray(data?.['hydra:member'])
                ? data['hydra:member']
                : [];
        // Map API fields to existing GracenoteTeam shape used in UI
        return member.map((t: any) => ({
            id: t.id,
            team_brand_name: t.teamBrandName,
            nick_name: t.nickName,
            proper_name: t.properName,
            abbreviation: t.abbreviation,
            team_brand_id: t.teamId, // best-effort mapping
            team_id: t.teamId,
            sport: {
                id: Number(String(t.sport || '').split('/').pop()),
                label: '',
            } as any,
            team_organization: {
                id: Number(String(t.teamOrganization || '').split('/').pop()),
                organization_name: '',
                organization_id: '',
            } as any,
        }));
    } catch (error) {
        console.warn("Teams API not available, returning empty array:", error);
        return [];
    }
};



interface Channel {
    stationId: string;
    callSign: string;
    videoQuality: {
        signalType: string;
        truResolution: string;
        videoType: string;
    };
    affiliateCallSign: string;
    affiliateId: string;
    channel: string;
    preferredImage: {
        width: string;
        height: string;
        uri: string;
        category: string;
        primary: string;
    };
}

/** Deduplicate channels by channel number; first occurrence wins. */
export const dedupeChannelsByNumber = (channels: Channel[]): Channel[] => {
    const seen = new Set<string>();
    return channels.filter((channel) => {
        const key = String(channel.channel ?? "").trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/**
 * Append static TV package channels (converted to Gracenote Channel shape)
 * onto an API channel list, then dedupe by channel number.
 */
export const mergeAdditionalGracenoteChannels = (
    apiChannels: Channel[] = []
): Channel[] => {
    const additional = additionalGracenoteChannels as Channel[];
    return dedupeChannelsByNumber([...apiChannels, ...additional]);
};

/** Lookup map of additional channels by channel number for save payloads. */
export const getAdditionalChannelLookup = (): Record<
    string,
    { callSign: string; stationId: string }
> => {
    return (additionalGracenoteChannels as Channel[]).reduce(
        (acc, ch) => {
            const key = String(ch.channel ?? "").trim();
            if (key && !acc[key]) {
                acc[key] = { callSign: ch.callSign, stationId: ch.stationId };
            }
            return acc;
        },
        {} as Record<string, { callSign: string; stationId: string }>
    );
};

// Fetch Gracenote channels by lineup ID
export const fetchGracenoteChannels = async (
    lineupID: string
): Promise<Channel[]> => {
    const response = await apiService.get<Channel[]>(
        `/workplaces/gracenote/channels`,
        { params: { page: 1, itemsPerPage: 500, lineup: lineupID } }
    );
    const apiChannels = response.data ?? [];

    // Extra TV-package channels (NFL / Mix) belong to DIRECTV (DITV) lineups only
    const isDirectTvLineup = lineupID.includes("DITV");

    return isDirectTvLineup
        ? mergeAdditionalGracenoteChannels(apiChannels)
        : dedupeChannelsByNumber(apiChannels);
};


// localStorage keys for caching
const PREFERENCE_CHANNELS_STORAGE_KEY = 'preference_channels_cache';
const TV_PACKAGE_CHANNELS_STORAGE_KEY = 'tv_package_channels_cache';

// Function to clear preference channels cache
export const clearPreferenceChannelsCache = (workplaceId?: number) => {
    if (workplaceId) {
        localStorage.removeItem(`${PREFERENCE_CHANNELS_STORAGE_KEY}_${workplaceId}`);
    } else {
        // Clear all preference channels cache entries
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(PREFERENCE_CHANNELS_STORAGE_KEY)) {
                localStorage.removeItem(key);
            }
        });
    }
};

// Function to clear TV package channels cache
export const clearTvPackageChannelsCache = (workplaceId?: number) => {
    if (workplaceId) {
        localStorage.removeItem(`${TV_PACKAGE_CHANNELS_STORAGE_KEY}_${workplaceId}`);
    } else {
        // Clear all TV package channels cache entries
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(TV_PACKAGE_CHANNELS_STORAGE_KEY)) {
                localStorage.removeItem(key);
            }
        });
    }
};

// Fetch saved channel preferences for a workplace
export const fetchPreferenceChannels = async (
    workplaceId: number,
    itemsPerPage: number = 500,
    useCache: boolean = true
): Promise<Channel[]> => {
    // Check localStorage first
    if (useCache) {
        const cachedData = localStorage.getItem(`${PREFERENCE_CHANNELS_STORAGE_KEY}_${workplaceId}`);
        if (cachedData) {
            try {
                const parsedData = JSON.parse(cachedData);
                if (parsedData && parsedData.channels && parsedData.channels.length > 0) {
                    return parsedData.channels;
                }
            } catch (error) {
                console.warn("Error parsing cached preference channels:", error);
            }
        }
    }

    const response = await apiService.get<any>(
        `/game_master_preference_channels`,
        { params: { page: 1, itemsPerPage, workplace: workplaceId } }
    );

    const member = Array.isArray(response.data?.member) ? response.data.member : [];

    // Normalize to Channel-like objects the UI can reason about
    const channels = member.map((m: any) => ({
        id: m.id,
        lineup: m.lineup,
        lineupPackage: m.lineupPackage || m.lineup_package || "",
        lineupAddon: m.lineupAddon || m.lineup_addon || "",
        channel: m.channel,
        callSign: m.callSign ?? null,
        stationId: m.stationId ?? null,
    }));

    // Store in localStorage with quota handling
    const cacheData = JSON.stringify({
        channels,
        timestamp: Date.now()
    });
    
    if (!safeSetItem(`${PREFERENCE_CHANNELS_STORAGE_KEY}_${workplaceId}`, cacheData)) {
        console.warn('Failed to cache preference channels due to quota exceeded');
    }

    return channels;
};

export const fetchChannels = async (
    workplaceId: number,
    itemsPerPage: number = 500,
    useCache: boolean = true
): Promise<Channel[]> => {
    console.log('fetchChannels called:', { workplaceId, itemsPerPage, useCache });
    
    // Check localStorage first
    if (useCache) {
        const cachedData = localStorage.getItem(`${TV_PACKAGE_CHANNELS_STORAGE_KEY}_${workplaceId}`);
        if (cachedData) {
            try {
                const parsedData = JSON.parse(cachedData);
                if (parsedData && parsedData.channels && parsedData.channels.length > 0) {
                    console.log('fetchChannels: Using cached data, channels:', parsedData.channels.length);
                    return parsedData.channels;
                }
            } catch (error) {
                console.warn("Error parsing cached TV package channels:", error);
            }
        } else {
            console.log('fetchChannels: No cache found, will call API');
        }
    } else {
        console.log('fetchChannels: Cache disabled, will call API');
    }

    console.log('fetchChannels: Calling API /tv_package_channels');
    const response = await apiService.get<any>(
        `/tv_package_channels`,
        { params: { page: 1, itemsPerPage, workplace: workplaceId } }
    );

    const member = Array.isArray(response.data?.member) ? response.data.member : [];
    console.log('fetchChannels: API response received, channels:', member.length);

    // Store in localStorage with quota handling
    // Store all channels, not just 200, to ensure full data is available
    const cacheData = JSON.stringify({
        channels: member, // Store all channels
        timestamp: Date.now(),
        totalCount: member.length
    });
    
    if (!safeSetItem(`${TV_PACKAGE_CHANNELS_STORAGE_KEY}_${workplaceId}`, cacheData)) {
        console.warn('Failed to cache TV package channels due to quota exceeded. Clearing old cache entries.');
        // Clear old cache entries and try once more
        clearOldCacheEntriesByCount(TV_PACKAGE_CHANNELS_STORAGE_KEY, 1);
        safeSetItem(`${TV_PACKAGE_CHANNELS_STORAGE_KEY}_${workplaceId}`, cacheData);
    } else {
        console.log('fetchChannels: Data cached successfully in localStorage');
    }

    // Normalize to Channel-like objects the UI can reason about
    return member;
};




interface LegacyChannel {
    channel: string;
    call_sign: string | null;
    station_id: string | null;
}

interface LineupPayload {
    lineup: string;
    channels: LegacyChannel[];
}

export const useChannelsMutation = () => {
    const workplaceId = Number(localStorage.getItem("workplace-id"));

    return useMutation({
        mutationFn: (payload: any[]) => {
            // Payload should be array of { lineup, lineup_package, lineup_addon, channels: [...] }
            // Format for API:
            // {
            //   workplace: { id },
            //   channels: [{ lineup, lineup_package, lineup_addon, channel, call_sign, station_id }]
            // }
            const requestBody = {
                workplace: { id: workplaceId },
                channels: payload.flatMap((item) =>
                    item.channels.map((channelObj: any) => ({
                        lineup: item.lineup,
                        lineup_package: item.lineup_package || "",
                        lineup_addon: item.lineup_addon || "",
                        channel: channelObj.channel,
                        call_sign: channelObj.call_sign ?? null,
                        station_id: channelObj.station_id ?? null,
                    }))
                ),
            };

            return apiService.post(`/game_master_preference_channels/single_lineup`, requestBody);
        },
    });
};
















